import React from 'react';
import AdminButton from './AdminButton';

/**
 * AdminEmptyState
 * 
 * Standardized empty state component for admin pages.
 * 
 * @param {string} title - Empty state title
 * @param {string} description - Optional description
 * @param {ReactNode} action - Optional action button
 * @param {string} variant - Empty state variant: 'empty' | 'error' | 'no-results' (default: 'empty')
 * @param {string} icon - Optional icon/emoji to display
 */
export default function AdminEmptyState({ 
  title, 
  description, 
  action,
  variant = 'empty',
  icon
}) {
  // Default icons by variant
  const defaultIcons = {
    empty: '📭',
    error: '⚠️',
    'no-results': '🔍',
  };
  
  const displayIcon = icon || defaultIcons[variant] || defaultIcons.empty;
  const variantClass = `admin-empty-state-${variant}`;

  return (
    <div className={`admin-empty-state ${variantClass}`} style={styles.container}>
      <div style={styles.content}>
        {displayIcon && (
          <div className="admin-empty-state-icon" style={styles.icon}>
            {displayIcon}
          </div>
        )}
        <h3 style={styles.title}>{title}</h3>
        {description && <p style={styles.description}>{description}</p>}
        {action && <div style={styles.action}>{action}</div>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: 'var(--spacing-8)', // 64px - reduced from 48px (3xl) to match design system
    textAlign: 'center',
    color: 'var(--color-text-tertiary)',
  },
  content: {
    maxWidth: '400px',
    margin: '0 auto',
  },
  title: {
    fontSize: 'var(--font-size-lg)', // 17px - matches h3
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-2)', // 16px
    color: 'var(--color-text-secondary)',
    margin: '0 0 var(--spacing-2) 0',
  },
  description: {
    fontSize: 'var(--font-size-sm)', // 14px
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-4)', // 32px
    color: 'var(--color-text-tertiary)',
    margin: '0 0 var(--spacing-4) 0',
  },
  action: {
    marginTop: 'var(--spacing-4)', // 32px
  },
};

